import React, {
    useState,
    useMemo
} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Button,
    Stack,
    Paper,
    Divider,
} from "@mui/material";
import {
    CloudUploadOutlined,
    DownloadOutlined,
    UploadFileOutlined,
} from "@mui/icons-material"
import {
    useBulkImportStudentsMutation
} from "../../Redux/actions/studentAction"
import CLoader from "../../components/Loader";
import _ from "lodash"
import { useSnackbar } from "../../components/Snakebar";

export default function ImportStudentsDialog({
    open,
    onClose,
    onDownloadTemplate,
    selectedClass
}) {
    const { showSnackbar } = useSnackbar()
    const [file, setFile] = useState(null)
    const [importStudentApi, importStudentStatus] = useBulkImportStudentsMutation()

    const importData = useMemo(() => {
        if (importStudentStatus.isError) {
            const errors = _.get(
                importStudentStatus,
                "data.errors",
                []
            );

            let errorData = errors
                .map(item => `Row ${item.row}: ${item.message}`)
                .join(", ")
            let message = _.get(
                importStudentStatus,
                "data.message",
                ""
            )
            let total = _.get(importStudentStatus, "data.total", "")
            let failed = _.get(importStudentStatus, "data.failed", "")
            let success = _.get(importStudentStatus, "data.success", "")
            showSnackbar({
                message: `Error while import the student list,total=${total} failed = ${failed} success=${success}`,
                severity: "error"
            })
            return {
                errorData,
                message,
                total,
                failed,
                success
            }
        } else if (importStudentStatus.isSuccess) {
            const errors = _.get(
                importStudentStatus,
                "data.errors",
                []
            );

            let errorData = errors
                .map(item => `Row ${item.row}: ${item.message}`)
                .join(", ")
            let message = _.get(
                importStudentStatus,
                "data.message",
                ""
            )
            let total = _.get(importStudentStatus, "data.total", "")
            let failed = _.get(importStudentStatus, "data.failed", "")
            let success = _.get(importStudentStatus, "data.success", "")
            showSnackbar({
                message: `Student list imported success,total=${total} failed = ${failed} success=${success}`,
                severity: "success"
            })
            setFile(null)
            onClose()
            return {
                errorData,
                message,
                total,
                failed,
                success
            }
        } else {
            return null
        }
    }, [importStudentStatus.status])
    function handleImport() {
        if (!selectedClass || !file) return
        importStudentApi({
            class_ref: selectedClass.id,
            school_ref: selectedClass.institution_ref,
            file: file
        })
    }
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle
                sx={{
                    fontWeight: 700,
                    fontSize: "1.5rem",
                }}
            >
                Import Students
            </DialogTitle>

            <DialogContent>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                >
                    Import multiple students at once using an Excel or CSV
                    file. Download the template, fill in the student data,
                    and upload it here.
                </Typography>

                {/* Download Template Section */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2.5,
                        borderRadius: 3,
                        mb: 3,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Box>
                            <Typography fontWeight={600}>
                                Student Import Template
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Download the latest template before importing
                                students.
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<DownloadOutlined />}
                            onClick={onDownloadTemplate}
                        >
                            Download Template
                        </Button>
                    </Stack>
                </Paper>

                {/* Upload Area */}
                <Box
                    sx={{
                        border: "2px dashed",
                        borderColor: "divider",
                        borderRadius: 3,
                        p: 5,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "0.2s ease",
                        "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                        },
                    }}
                >
                    <CloudUploadOutlined
                        color="primary"
                        sx={{
                            fontSize: 60,
                            mb: 2,
                        }}
                    />

                    <Typography
                        variant="h6"
                        fontWeight={600}
                        gutterBottom
                    >
                        Upload Student File
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Drag & drop your file here or browse from your device.
                    </Typography>

                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1 }}
                        color="text.secondary"
                    >
                        Supported formats: .xlsx, .xls, .csv
                    </Typography>

                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<UploadFileOutlined />}
                        sx={{ mt: 3 }}
                    >
                        Select File

                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={(e) => {
                                const selectedFile = e.target.files?.[0];

                                if (selectedFile) {
                                    setFile(selectedFile);
                                }
                            }}
                        />
                    </Button>
                    {
                        file && (
                            <Typography
                                variant="body2"
                                color="success.main"
                                sx={{ mt: 2 }}
                            >
                                Selected: {file.name}
                            </Typography>
                        )
                    }
                    {
                        importData && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: "background.paper",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography
                                    fontWeight={600}
                                    color="primary.main"
                                >
                                    Import Message: {importData.message}
                                </Typography>

                                <Typography
                                    fontWeight={500}
                                    color="text.primary"
                                >
                                    Total Students: {importData.total}
                                </Typography>

                                <Typography
                                    fontWeight={600}
                                    color="success.main"
                                >
                                    Total Success: {importData.success}
                                </Typography>

                                <Typography
                                    fontWeight={600}
                                    color="error.main"
                                >
                                    Total Failed: {importData.failed}
                                </Typography>

                                {
                                    importData.errorData && (
                                        <Typography
                                            sx={{
                                                mt: 1,
                                                p: 1.5,
                                                borderRadius: 1,
                                                bgcolor: "error.lighter",
                                                color: "error.main",
                                                whiteSpace: "pre-wrap",
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            Error Message: {importData.errorData}
                                        </Typography>
                                    )
                                }
                            </Box>
                        )
                    }
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Instructions */}
                <Box>
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        mb={1}
                    >
                        Import Instructions
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        • Download the student template.
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        • Do not change column headers.
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        • Ensure Roll Number and Admission Number are unique.
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        • Mandatory fields must not be left blank.
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        • Upload the completed file and review errors before
                        confirming.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        importStudentStatus.reset()
                        onClose()
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    startIcon={<UploadFileOutlined />}
                    onClick={handleImport}
                >
                    Import Students
                </Button>
            </DialogActions>
            <CLoader
                show={importStudentStatus.isLoading}
                message={"Importing..."}
            />
        </Dialog>
    );
}